import { JsonPointer as jsonpointer } from "json-ptr"

interface ReferenceableMap {
    rowKey: string
    referenceableKey: string
}

/**
 * Parameters accepted by jsonPointerValueGetter. Matches the shape of
 * AG-Grid ValueGetterParams but only requires the subset of properties
 * actually used by the function.
 */
export interface JsonPointerValueGetterParams {
    data?: Record<string, unknown> | null
    colDef: {
        field?: string
        referenceableMap?: ReferenceableMap
    }
    context?: {
        settings?: {
            otherSettings?: {
                requestSpecificFields?: boolean
            }
        }
        referenceableData?: Record<string, Record<string, Record<string, unknown>>>
    }
}

/**
 * Extracts a value from row data using JSON pointer field access.
 * Supports direct field access, referenceable lookups, and request-specific fields.
 *
 * @param params - AG-Grid-like params containing row data, column definition, and context
 * @returns The resolved value from the row data, or undefined if not found
 */
export const jsonPointerValueGetter = (params: JsonPointerValueGetterParams): unknown => {
    const {
        data,
        colDef: { field, referenceableMap },
        context: { settings: { otherSettings: { requestSpecificFields } = {} } = {}, referenceableData } = {},
    } = params
    if (requestSpecificFields && data && field && field in data) {
        return data[field]
    }

    let resolvedData = data
    if (referenceableMap && data) {
        let key = data[referenceableMap.rowKey]
        if (!key && referenceableMap.rowKey.startsWith("/")) key = jsonpointer.has(data, referenceableMap.rowKey) ? jsonpointer.get(data, referenceableMap.rowKey) : undefined
        if (typeof key === "object") {
            resolvedData = key as Record<string, unknown>
        } else {
            // In the event of a Guest Form Share, there may or may not be referenceable data
            if (key && referenceableData) {
                resolvedData = referenceableData[referenceableMap.referenceableKey][key as string]
            } else {
                return undefined
            }
        }
    }
    return field && jsonpointer.has(resolvedData, field) ? jsonpointer.get(resolvedData, field) : undefined
}
